/**

 * Upload Resource Access Configuration

 *

 * WASA: Malicious Upload through Response Manipulation

 * - Access is DENIED BY DEFAULT.

 * - New uploads: validate canonical document type (name/code) + file type + size.

 * - Existing document edits: validate document id + file type only (labels may be legacy).

 * - Server must enforce ownership on PUT /documents/:id (authorizeResourceOwner).

 */



/** Allowed file type keys as returned by API. Only these are permitted. */

export const ALLOWED_UPLOAD_FILE_TYPES = Object.freeze(['PDF', 'Image']);



/** Max file size in MB. Server-sent fileSize is capped by this. */

export const MAX_UPLOAD_FILE_SIZE_MB = 5;



/** Accept attribute per file type (for FileUpload). Declared in code, not from server. */

export const UPLOAD_ACCEPT_BY_TYPE = Object.freeze({

    PDF: '.pdf',

    Image: '.jpg,.jpeg,.png,.JPG,.JPEG,.PNG',

});



/**

 * Stable document type codes (preferred over display labels for validation).

 * Map code -> canonical display name used for new uploads.

 */

export const DOCUMENT_TYPE_CODES = Object.freeze({

    PHOTOGRAPH: 'Photograph with white background',

    SPECIMEN_SIGNATURE: 'Specimen Signature',

    GOVERNMENT_EMPLOYEES_ID: 'Government Employees- Appointment Letter/ ID Card',

    PROOF_OF_AGE: 'Self-attested proof of age',

    PROOF_OF_RESIDENCE: 'Self-attested proof of residence/domicile certificate',

    INTERNSHIP_CERTIFICATE: 'Self-attested Internship Certificate',

    BNYS_DEGREE: 'Self-attested copy of BNYS Degree',

    STATE_BOARD_CERTIFICATES: 'Self-attested copy of State Board Certificates',

    ADDITIONAL_QUALIFICATION_BNYS: 'Self-attested additional qualification to BNYS Certificate (if applicable)',

    NAME_CHANGE_DECLARATION: 'Self-attested Marriage Certificate/Newspaper Advt/affidavit to declare the name change',

    CENTRAL_REGISTRATION_CERTIFICATE: 'Self-attested copy of Central registration certificate (if applicable)',

});



/**

 * Document names allowed for new uploads (canonical labels).

 * When the backend adds a new document type, add its code above and name here.

 */

export const ALLOWED_DOCUMENT_NAMES = Object.freeze(Object.values(DOCUMENT_TYPE_CODES));



/**

 * Legacy display labels stored on old user rows (e.g. Old certificate, 2023).

 * Maps legacy label -> canonical name in ALLOWED_DOCUMENT_NAMES.

 */

export const LEGACY_DOCUMENT_NAME_ALIASES = Object.freeze({

    'Specimen Signature  (in Black Ink)': DOCUMENT_TYPE_CODES.SPECIMEN_SIGNATURE,

    'Self-attested additional qualification degree Certificate':

        DOCUMENT_TYPE_CODES.ADDITIONAL_QUALIFICATION_BNYS,

});



/** Legacy-only canonical names (no alias needed; stored verbatim in DB). */

const LEGACY_CANONICAL_NAMES = new Set([

    DOCUMENT_TYPE_CODES.CENTRAL_REGISTRATION_CERTIFICATE,

]);



/**

 * Resolves a server-provided documentName to its canonical allowlist label.

 * @param {string} documentName

 * @returns {string}

 */

export function resolveCanonicalDocumentName(documentName) {

    const trimmed = typeof documentName === 'string' ? documentName.trim() : '';

    if (!trimmed) return '';

    return LEGACY_DOCUMENT_NAME_ALIASES[trimmed] ?? trimmed;

}



/**

 * Resolves stable document type code from display name (canonical or legacy).

 * @param {string} documentName

 * @returns {string|null}

 */

export function resolveDocumentTypeCode(documentName) {

    const canonical = resolveCanonicalDocumentName(documentName);

    if (!canonical) return null;



    for (const [code, name] of Object.entries(DOCUMENT_TYPE_CODES)) {

        if (name === canonical) return code;

    }

    return null;

}



/**

 * @param {unknown} id

 * @returns {boolean}

 */

function isValidExistingDocumentId(id) {

    if (id == null || id === '') return false;

    const numId = Number(id);

    return Number.isInteger(numId) && numId > 0;

}



/**

 * Validates server-provided rowData against the allowlist.

 * Use the returned values for accept/maxSize; never use rowData.fileType or rowData.fileSize directly for security.

 *

 * @param {Object} rowData - Resource from API (may be manipulated)

 * @returns {{ allowed: boolean, accept?: string, maxSizeMB?: number, fileType?: string, documentTypeCode?: string, canonicalDocumentName?: string, error?: string }}

 */

export function validateUploadResource(rowData) {

    const defaultDeny = { allowed: false, error: 'Access denied. Resource not allowed.' };



    if (!rowData || typeof rowData !== 'object') {

        return defaultDeny;

    }



    const fileType = rowData.fileType;

    const fileSize = Number(rowData.fileSize);

    const rawDocumentName = typeof rowData.documentName === 'string' ? rowData.documentName.trim() : '';

    const id = rowData.id;

    const isEditExistingDocument = isValidExistingDocumentId(id);



    if (id != null && id !== '' && !isEditExistingDocument) {

        return { ...defaultDeny, error: 'Invalid document id.' };

    }



    const normalizedFileType = typeof fileType === 'string' ? fileType.trim() : '';

    if (!ALLOWED_UPLOAD_FILE_TYPES.includes(normalizedFileType)) {

        return { ...defaultDeny, error: 'Invalid file type for this resource.' };

    }



    const accept = UPLOAD_ACCEPT_BY_TYPE[normalizedFileType];

    if (!accept) {

        return defaultDeny;

    }



    // Existing rows: id + file type only (ownership enforced on server)

    if (isEditExistingDocument) {

        const effectiveMaxSizeMB = MAX_UPLOAD_FILE_SIZE_MB;

        const documentTypeCode = rawDocumentName ? resolveDocumentTypeCode(rawDocumentName) : null;

        const canonicalDocumentName = rawDocumentName ? resolveCanonicalDocumentName(rawDocumentName) : '';



        return {

            allowed: true,

            accept,

            maxSizeMB: effectiveMaxSizeMB,

            fileType: normalizedFileType,

            documentTypeCode: documentTypeCode ?? undefined,

            canonicalDocumentName: canonicalDocumentName || undefined,

        };

    }



    // New upload: require canonical document name (via alias map), not loose includes()

    if (!rawDocumentName) {

        return { ...defaultDeny, error: 'Invalid document name.' };

    }



    const canonicalDocumentName = resolveCanonicalDocumentName(rawDocumentName);

    const documentTypeCode = resolveDocumentTypeCode(rawDocumentName);



    const isAllowedCanonical =

        ALLOWED_DOCUMENT_NAMES.includes(canonicalDocumentName) ||

        LEGACY_CANONICAL_NAMES.has(canonicalDocumentName);



    if (!isAllowedCanonical || !documentTypeCode) {

        return { ...defaultDeny, error: 'This document type is not allowed for upload.' };

    }



    if (!Number.isFinite(fileSize) || fileSize <= 0 || fileSize > MAX_UPLOAD_FILE_SIZE_MB) {

        return {

            ...defaultDeny,

            error: `File size must be between 1 and ${MAX_UPLOAD_FILE_SIZE_MB} MB.`,

        };

    }



    const effectiveMaxSizeMB = Math.min(fileSize, MAX_UPLOAD_FILE_SIZE_MB);



    return {

        allowed: true,

        accept,

        maxSizeMB: effectiveMaxSizeMB,

        fileType: normalizedFileType,

        documentTypeCode,

        canonicalDocumentName,

    };

}


